import React, { useState, useEffect, useMemo } from 'react';
import { subMonths, format } from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE } from '../../config';
import { useLocale } from '../../contexts/LocaleContext';
import { DatePicker } from '../DatePicker';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import {
  ADMIN_CHART_TOOLTIP_STYLE,
  ADMIN_EMPTY_BOX_CLASSNAME,
  ADMIN_INLINE_SELECT_CLASSNAME,
  ADMIN_MUTED_TEXT_CLASSNAME,
  ADMIN_SECTION_TITLE_CLASSNAME,
} from './admin-ui';

type GroupBy = 'day' | 'week' | 'month';

interface TimelineEntry {
  period: string;
  creditsUsed: number;
  creditsAdded: number;
}

function getDefaultDateRange(): { startDate: string; endDate: string } {
  const end = new Date();
  const start = subMonths(end, 3);
  return {
    startDate: format(start, 'yyyy-MM-dd'),
    endDate: format(end, 'yyyy-MM-dd'),
  };
}

function getLocaleCode(locale: string): string {
  switch (locale) {
    case 'es':
      return 'es-ES';
    case 'ca':
      return 'ca-ES';
    case 'de':
      return 'de-DE';
    case 'fr':
      return 'fr-FR';
    default:
      return 'en-US';
  }
}

function formatPeriodLabel(period: string, groupBy: GroupBy, localeCode: string): string {
  const date = new Date(`${period}T00:00:00.000Z`);
  if (groupBy === 'month') {
    return date.toLocaleDateString(localeCode, { month: 'short', year: 'numeric', timeZone: 'UTC' });
  }
  return date.toLocaleDateString(localeCode, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

interface UserCreditsActivityChartProps {
  userId: string;
}

export const UserCreditsActivityChart: React.FC<UserCreditsActivityChartProps> = ({ userId }) => {
  const { token, masterKey } = useAuth();
  const { t, locale } = useLocale();
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [groupBy, setGroupBy] = useState<GroupBy>('week');
  const [includeRefills, setIncludeRefills] = useState(false);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTimeline = async () => {
      if (!userId || (!token && !masterKey) || !startDate || !endDate) {
        return;
      }

      setLoading(true);
      try {
        const params = new URLSearchParams({
          userId,
          groupBy,
          startDate,
          endDate,
        });
        if (masterKey) {
          params.append('masterKey', masterKey);
        }
        const url = `${API_BASE}/api/admin-credits-activity/timeline?${params.toString()}`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (response.ok) {
          const data = await response.json();
          setTimeline(data.timeline || []);
        } else {
          setTimeline([]);
        }
      } catch (err) {
        console.error('Error fetching credits timeline:', err);
        setTimeline([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTimeline();
  }, [userId, token, masterKey, groupBy, startDate, endDate]);

  const localeCode = useMemo(() => getLocaleCode(locale), [locale]);

  const chartData = useMemo(
    () =>
      timeline.map((entry) => ({
        period: entry.period,
        label: formatPeriodLabel(entry.period, groupBy, localeCode),
        creditsUsed: entry.creditsUsed,
        creditsAdded: entry.creditsAdded,
      })),
    [timeline, groupBy, localeCode],
  );

  const hasData = chartData.some((entry) => entry.creditsUsed > 0 || entry.creditsAdded > 0);

  return (
    <div className="mb-6">
      <h3 className={ADMIN_SECTION_TITLE_CLASSNAME}>
        {t('credits_activity_chart')}
      </h3>

      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label htmlFor="credits-chart-start-date" className={ADMIN_MUTED_TEXT_CLASSNAME}>
            {t('start_date')}:
          </label>
          <DatePicker
            id="credits-chart-start-date"
            value={startDate}
            onChange={setStartDate}
          />
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="credits-chart-end-date" className={ADMIN_MUTED_TEXT_CLASSNAME}>
            {t('end_date')}:
          </label>
          <DatePicker
            id="credits-chart-end-date"
            value={endDate}
            onChange={setEndDate}
          />
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="credits-chart-group-by" className={ADMIN_MUTED_TEXT_CLASSNAME}>
            {t('credits_group_by')}:
          </label>
          <select
            id="credits-chart-group-by"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as GroupBy)}
            className={ADMIN_INLINE_SELECT_CLASSNAME}
          >
            <option value="day">{t('credits_group_by_day')}</option>
            <option value="week">{t('credits_group_by_week')}</option>
            <option value="month">{t('credits_group_by_month')}</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="credits-chart-include-refills"
            checked={includeRefills}
            onCheckedChange={(checked) => setIncludeRefills(checked === true)}
          />
          <Label htmlFor="credits-chart-include-refills" className="cursor-pointer text-sm text-muted-foreground">
            {t('include_refills')}
          </Label>
        </div>
      </div>

      {loading ? (
        <div className={`py-8 text-center ${ADMIN_MUTED_TEXT_CLASSNAME}`}>{t('loading')}</div>
      ) : !hasData ? (
        <div className={ADMIN_EMPTY_BOX_CLASSNAME}>
          {t('no_data')}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="label" stroke="var(--muted-foreground)" tick={{ fill: 'var(--muted-foreground)' }} />
            <YAxis stroke="var(--muted-foreground)" tick={{ fill: 'var(--muted-foreground)' }} />
            <Tooltip
              {...ADMIN_CHART_TOOLTIP_STYLE}
            />
            <Legend />
            <Bar dataKey="creditsUsed" name={t('credits_usage')} fill="#dc2626" />
            {includeRefills && (
              <Bar dataKey="creditsAdded" name={t('credits_added')} fill="#059669" />
            )}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};
